import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  Typography, 
  Box,
  Paper, 
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { getAllUsersNoPagination, getCurrentUser } from '../../services/userService';
import { getAllTeams } from '../../services/teamService';
import { getAllProjectsNoPagination } from '../../services/projectService';
import { createAdminTimecard, deleteTimecard } from '../../services/timecardService';
import { getWeeksInYear, getWeekDates, parseUTCDate } from '../../utils/dateUtils';
import EditTimecardAdminDialog from './EditTimecardAdminDialog';

const CreateTimecards = () => {
  // State for form controls
  const [userId, setUserId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedWeek, setSelectedWeek] = useState('');
  
  // Data state
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [userProjects, setUserProjects] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createdTimecards, setCreatedTimecards] = useState([]);
  const [selectedTimecard, setSelectedTimecard] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUserData, setSelectedUserData] = useState(null);

  const { user: currentUser, setUser } = useAuth();

  // Load users, teams, and projects
  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('Loading data with current user:', currentUser);
        
        // Force refresh user data from API to get latest team information
        let freshUserData = currentUser;
        try {
          freshUserData = await getCurrentUser();
          console.log('Fresh user data from API:', freshUserData);
          setUser(freshUserData); // Update the auth context
        } catch (error) {
          console.warn('Error refreshing user data, using current user:', error);
        }
        
        if (!freshUserData) {
          console.warn('No user data available, waiting...');
          return;
        }

        // Load other data
        const [usersData, teamsData, projectsData] = await Promise.all([
          getAllUsersNoPagination(),
          getAllTeams(),
          getAllProjectsNoPagination()
        ]);

        // Filter out admin and accountant users, and only show active users
        let filteredUsers = usersData.filter(user => 
          !['admin', 'accountant'].includes(user.role) && user.isActive === true
        );

        if (freshUserData.role === 'team_lead') {
          console.log('Filtering for team lead:', freshUserData.username, 'teamMembers:', freshUserData.teamMembers);
          // For team leads, use the teamMembers array that should be populated by the auth middleware
          // This array contains all members of teams that the current user leads
          if (freshUserData.teamMembers && freshUserData.teamMembers.length > 0) {
            // Filter users to only show the team lead and their team members
            const currentUserId = freshUserData._id || freshUserData.id; // Handle both _id and id fields
            filteredUsers = filteredUsers.filter(user => 
              String(user._id) === String(currentUserId) || // Self
              freshUserData.teamMembers.some(id => String(id) === String(user._id)) // Team members
            );
            console.log('Filtered users for team lead:', filteredUsers.map(u => u.username));
          } else {
            console.log('No team members found, showing only self');
            // If no team members, only show the team lead themselves
            const currentUserId = freshUserData._id || freshUserData.id;
            filteredUsers = filteredUsers.filter(user => 
              String(user._id) === String(currentUserId)
            );
          }
        }

        // Sort users by name
        const sortedUsers = filteredUsers.sort((a, b) => {
          const nameA = `${a.firstName} ${a.lastName}`;
          const nameB = `${b.firstName} ${b.lastName}`;
          return nameA.localeCompare(nameB);
        });

        setUsers(sortedUsers);
        setTeams(teamsData);
        setAllProjects(projectsData);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data');
      }
    };
    
    // Run when component mounts and when current user changes
    if (currentUser) {
      loadData();
    }
  }, [currentUser?.username, setUser]); // Only depend on username to avoid infinite loops

  // Filter projects when user changes
  useEffect(() => {
    if (!userId || !teams.length || !allProjects.length) {
      setUserProjects([]);
      setProjectId('');
      setSelectedUserData(null);
      return;
    }

    try {
      // Find the selected user
      const selectedUser = users.find(user => user._id === userId);
      
      // Store the selected user data for later use
      setSelectedUserData(selectedUser);

      if (!selectedUser) {
        setUserProjects([]);
        return;
      }

      // Method 1: Try to get teams from user's memberTeam field
      let userTeams = [];
      if (selectedUser.memberTeam && Array.isArray(selectedUser.memberTeam) && selectedUser.memberTeam.length > 0) {
        const userTeamIds = selectedUser.memberTeam.map(team => typeof team === 'object' ? team._id : team);
        userTeams = teams.filter(team => userTeamIds.includes(team._id));
      } else if (selectedUser.memberTeam && !Array.isArray(selectedUser.memberTeam)) {
        // Handle case where memberTeam is a single team object/ID instead of array
        const teamId = typeof selectedUser.memberTeam === 'object' ? selectedUser.memberTeam._id : selectedUser.memberTeam;
        userTeams = teams.filter(team => team._id === teamId);
      }

      // Method 2: If no teams found via memberTeam, search teams for this user as a member
      if (userTeams.length === 0) {
        userTeams = teams.filter(team => {
          if (!team.members || team.members.length === 0) {
            return false;
          }
          
          // Check if the user is in this team's members array
          return team.members.some(member => {
            const memberId = typeof member === 'object' ? member._id : member;
            return memberId === selectedUser._id;
          });
        });
      }

      if (userTeams.length === 0) {
        setUserProjects([]);
        return;
      }

      // Create a map of project IDs to full project objects
      const projectMap = new Map(allProjects.map(project => [project._id, project]));

      // Collect all projects from all teams
      let allTeamProjects = [];
      userTeams.forEach(team => {
        if (team.projects && Array.isArray(team.projects)) {
          allTeamProjects = [...allTeamProjects, ...team.projects];
        }
      });

      // Get full project objects and remove duplicates
      const projectIds = new Set();
      const filteredProjects = [];
      
      allTeamProjects.forEach(teamProject => {
        const projectId = typeof teamProject === 'object' ? teamProject._id : teamProject;
        if (!projectIds.has(projectId)) {
          const project = projectMap.get(projectId);
          if (project) {
            projectIds.add(projectId);
            filteredProjects.push(project);
          }
        }
      });

      // Sort filtered projects by name before setting state
      const sortedProjects = filteredProjects.sort((a, b) => a.name.localeCompare(b.name));
      setUserProjects(sortedProjects);
      setProjectId(''); // Reset project selection when user changes
    } catch (err) {
      console.error('Error filtering projects:', err);
      setError('Failed to filter projects');
    }
  }, [userId, users, teams, allProjects]);

  // Generate weeks when year is selected
  useEffect(() => {
    if (selectedYear) {
      const year = parseInt(selectedYear);
      
      // Get weeks for the selected year
      const yearWeeks = getWeeksInYear(year);
      setWeeks(yearWeeks);
    }
  }, [selectedYear]);

  const handleCreate = async () => {
    try {
      setError('');
      setSuccess('');

      if (!userId || !projectId || !selectedYear || !selectedWeek) {
        setError('Please fill in all fields');
        return;
      }

      const createdTimecard = await createAdminTimecard(userId, projectId, selectedYear, selectedWeek);
      
      // Format the created timecard with proper structure
      const weekInfo = getWeekDates(parseUTCDate(createdTimecard.startDate));
      const formattedTimecard = {
        ...createdTimecard,
        dateRange: weekInfo.dateRange,
        user: selectedUserData,
        days: createdTimecard.days.map(day => ({
          ...day,
          hours: parseFloat(day.hours || 0).toFixed(2),
          comments: day.comments || '',
          certified: day.certified || false,
          submitted: day.submitted || false,
          approved: day.approved || false
        }))
      };
      
      // Add to created timecards list
      setCreatedTimecards([formattedTimecard]);
      setSuccess('Timecard created successfully');
      
      // Don't reset form - keep selections so user can see what they created
      
    } catch (err) {
      console.error('Error creating timecard:', err);
      setError(err.message || 'Failed to create timecard');
    }
  };

  const handleEdit = (timecard) => {
    setSelectedTimecard(timecard);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteTimecard(id, true);
      setCreatedTimecards(createdTimecards.filter(tc => tc._id !== id));
      setError(null);
      setSuccess('Timecard deleted successfully');
    } catch (err) {
      console.error('Error deleting timecard:', err);
      setError(err.message || 'Failed to delete timecard');
      setSuccess('');
    }
  };

  const handleUpdate = (updatedTimecard) => {
    const formattedTimecard = {
      ...updatedTimecard,
      days: updatedTimecard.days.map(day => ({
        ...day,
        hours: parseFloat(day.hours || 0).toFixed(2),
        comments: day.comments || '',
        certified: day.certified || false,
        submitted: day.submitted || false,
        approved: day.approved || false
      }))
    };
    
    setCreatedTimecards(createdTimecards.map(tc => 
      tc._id === updatedTimecard._id ? formattedTimecard : tc
    ));
  };

  return (
    <Box sx={{ 
      p: 3,
      '@media (min-width:601px) and (max-width:1199px)': {
        p: 2, // Moderate padding for tablets
        paddingTop: 1, // Reduced top padding for tablets
        paddingBottom: 2, // Normal bottom padding for tablets
      },
      '@media (max-width:600px)': {
        p: 1, // Much more reduced padding for mobile
        paddingTop: 0, // No top padding - layout provides spacing
        paddingBottom: '88px', // Account for bottom navigation
      },
      '@media (max-width:600px) and (orientation: landscape)': {
        p: 0.5, // Minimal padding in landscape mode
        paddingTop: 0, // No top padding
        paddingBottom: 0.5, // Minimal bottom padding
        height: 'auto', // Allow natural height
        overflow: 'visible', // Allow content to overflow
        minHeight: 'auto', // Remove height constraints
      }
    }}>
      {/* Instructions */}
      <Typography variant="body1" gutterBottom sx={{ 
        mb: 2,
        '@media (min-width:601px) and (max-width:1199px)': {
          mb: 1.5, // Moderate margin for tablets
        },
        '@media (max-width:600px)': {
          mb: 1, // Much more reduced margin for mobile
        },
        '@media (max-width:600px) and (orientation: landscape)': {
          mb: 0.5, // Reduced margin in landscape mode
        }
      }}>
        Select the employee, project, year, and week to generate a timecard.
      </Typography>

      {/* Controls Bar */}
      <Paper sx={{ 
        p: 2,
        '@media (max-width:600px)': {
          p: 0, // Remove all padding on mobile
        }
      }}>
        {/* Desktop Layout */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          gap: 2,
          '@media (max-width:1199px)': {
            display: 'none', // Hide desktop layout on tablets and mobile
          }
        }}>
          {/* User Dropdown */}
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>User</InputLabel>
            <Select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              label="User"
            >
              <MenuItem value="">Select User</MenuItem>
              {users.map((user) => (
                <MenuItem key={user._id} value={user._id}>
                  {`${user.firstName} ${user.lastName}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Project Dropdown */}
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Project</InputLabel>
            <Select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              label="Project"
              disabled={!userId || userProjects.length === 0}
            >
              <MenuItem value="">Select Project</MenuItem>
              {userProjects.map((project) => (
                <MenuItem key={project._id} value={project._id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Year Dropdown */}
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear}
              onChange={(e) => {
                setSelectedYear(e.target.value);
                setSelectedWeek(''); // Reset week when year changes
              }}
              label="Year"
            >
              <MenuItem value="">Select Year</MenuItem>
              {[2023, 2024, 2025, 2026, 2027].map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Week Dropdown */}
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Week</InputLabel>
            <Select
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(e.target.value)}
              label="Week"
              disabled={!selectedYear}
            >
              <MenuItem value="">Select Week</MenuItem>
              {weeks.map((week) => (
                <MenuItem key={week.weekNumber} value={week.weekNumber}>
                  {`Week ${week.weekNumber}: ${week.startDate} - ${week.endDate}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Create Button */}
          <Button 
            variant="contained" 
            onClick={handleCreate}
            sx={{ ml: 'auto' }}
          >
            Create
          </Button>
        </Box>

        {/* Tablet Portrait Layout */}
        <Box sx={{ 
          display: 'none',
          '@media (min-width:601px) and (max-width:1199px) and (orientation: portrait)': {
            display: 'block',
            px: 2, // Add horizontal padding
            py: 2, // Add vertical padding
          }
        }}>
          {/* Dropdowns Column - Stack all 4 dropdowns vertically */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 2, 
            mb: 2
          }}>
            {/* User Dropdown */}
            <FormControl size="small" sx={{ width: '100%' }}>
              <InputLabel>User</InputLabel>
              <Select
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                label="User"
              >
                <MenuItem value="">Select User</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user._id} value={user._id}>
                    {`${user.firstName} ${user.lastName}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Project Dropdown */}
            <FormControl size="small" sx={{ width: '100%' }}>
              <InputLabel>Project</InputLabel>
              <Select
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                label="Project"
                disabled={!userId || userProjects.length === 0}
              >
                <MenuItem value="">Select Project</MenuItem>
                {userProjects.map((project) => (
                  <MenuItem key={project._id} value={project._id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Year Dropdown */}
            <FormControl size="small" sx={{ width: '100%' }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(e.target.value);
                  setSelectedWeek(''); // Reset week when year changes
                }}
                label="Year"
              >
                <MenuItem value="">Select Year</MenuItem>
                {[2023, 2024, 2025, 2026, 2027].map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Week Dropdown */}
            <FormControl size="small" sx={{ width: '100%' }}>
              <InputLabel>Week</InputLabel>
              <Select
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(e.target.value)}
                label="Week"
                disabled={!selectedYear}
              >
                <MenuItem value="">Select Week</MenuItem>
                {weeks.map((week) => (
                  <MenuItem key={week.weekNumber} value={week.weekNumber}>
                    {`Week ${week.weekNumber}: ${week.startDate} - ${week.endDate}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Create Button */}
          <Button 
            variant="contained" 
            onClick={handleCreate}
            size="medium"
            sx={{ width: '100%' }}
          >
            Create
          </Button>
        </Box>

        {/* Tablet Landscape Layout */}
        <Box sx={{ 
          display: 'none',
          '@media (min-width:601px) and (max-width:1199px) and (orientation: landscape)': {
            display: 'block',
            px: 2, // Add horizontal padding
            py: 1.5, // Add vertical padding
          }
        }}>
          {/* First Row - User and Project */}
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            mb: 2
          }}>
            {/* User Dropdown */}
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>User</InputLabel>
              <Select
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                label="User"
              >
                <MenuItem value="">Select User</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user._id} value={user._id}>
                    {`${user.firstName} ${user.lastName}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Project Dropdown */}
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Project</InputLabel>
              <Select
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                label="Project"
                disabled={!userId || userProjects.length === 0}
              >
                <MenuItem value="">Select Project</MenuItem>
                {userProjects.map((project) => (
                  <MenuItem key={project._id} value={project._id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Second Row - Year, Week, and Create Button */}
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            alignItems: 'center'
          }}>
            {/* Year Dropdown */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(e.target.value);
                  setSelectedWeek(''); // Reset week when year changes
                }}
                label="Year"
              >
                <MenuItem value="">Select Year</MenuItem>
                {[2023, 2024, 2025, 2026, 2027].map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Week Dropdown */}
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Week</InputLabel>
              <Select
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(e.target.value)}
                label="Week"
                disabled={!selectedYear}
              >
                <MenuItem value="">Select Week</MenuItem>
                {weeks.map((week) => (
                  <MenuItem key={week.weekNumber} value={week.weekNumber}>
                    {`Week ${week.weekNumber}: ${week.startDate} - ${week.endDate}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Create Button */}
            <Button 
              variant="contained" 
              onClick={handleCreate}
              size="small"
              sx={{ minWidth: 100 }}
            >
              Create
            </Button>
          </Box>
        </Box>

        {/* Mobile Portrait Layout */}
        <Box sx={{ 
          display: 'none',
          '@media (max-width:600px) and (orientation: portrait)': {
            display: 'block',
            px: 1, // Add horizontal padding
            py: 1.5, // Add vertical padding
          }
        }}>
          {/* First Row - User and Project */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 1.5, 
            mb: 2
          }}>
            {/* User Dropdown */}
            <FormControl size="small" sx={{ width: '100%' }}>
              <InputLabel>User</InputLabel>
              <Select
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                label="User"
              >
                <MenuItem value="">Select User</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user._id} value={user._id}>
                    {`${user.firstName} ${user.lastName}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Project Dropdown */}
            <FormControl size="small" sx={{ width: '100%' }}>
              <InputLabel>Project</InputLabel>
              <Select
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                label="Project"
                disabled={!userId || userProjects.length === 0}
              >
                <MenuItem value="">Select Project</MenuItem>
                {userProjects.map((project) => (
                  <MenuItem key={project._id} value={project._id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Second Row - Year and Week */}
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            mb: 2
          }}>
            {/* Year Dropdown */}
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(e.target.value);
                  setSelectedWeek(''); // Reset week when year changes
                }}
                label="Year"
              >
                <MenuItem value="">Select Year</MenuItem>
                {[2023, 2024, 2025, 2026, 2027].map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Week Dropdown */}
            <FormControl size="small" sx={{ flex: 2 }}>
              <InputLabel>Week</InputLabel>
              <Select
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(e.target.value)}
                label="Week"
                disabled={!selectedYear}
              >
                <MenuItem value="">Select Week</MenuItem>
                {weeks.map((week) => (
                  <MenuItem key={week.weekNumber} value={week.weekNumber}>
                    {`Week ${week.weekNumber}: ${week.startDate} - ${week.endDate}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Create Button */}
          <Button 
            variant="contained" 
            onClick={handleCreate}
            size="small"
            sx={{ width: '100%' }}
          >
            Create
          </Button>
        </Box>

        {/* Mobile Landscape Layout */}
        <Box sx={{ 
          display: 'none',
          '@media (max-width:600px) and (orientation: landscape)': {
            display: 'block',
            px: 1, // Add horizontal padding
            py: 1, // Add vertical padding
          }
        }}>
          {/* First Row - User and Project */}
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            mb: 1.5
          }}>
            {/* User Dropdown */}
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>User</InputLabel>
              <Select
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                label="User"
              >
                <MenuItem value="">Select User</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user._id} value={user._id}>
                    {`${user.firstName} ${user.lastName}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Project Dropdown */}
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Project</InputLabel>
              <Select
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                label="Project"
                disabled={!userId || userProjects.length === 0}
              >
                <MenuItem value="">Select Project</MenuItem>
                {userProjects.map((project) => (
                  <MenuItem key={project._id} value={project._id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Second Row - Year, Week, and Create Button */}
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            alignItems: 'center'
          }}>
            {/* Year Dropdown */}
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(e.target.value);
                  setSelectedWeek(''); // Reset week when year changes
                }}
                label="Year"
              >
                <MenuItem value="">Select Year</MenuItem>
                {[2023, 2024, 2025, 2026, 2027].map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Week Dropdown */}
            <FormControl size="small" sx={{ flex: 2 }}>
              <InputLabel>Week</InputLabel>
              <Select
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(e.target.value)}
                label="Week"
                disabled={!selectedYear}
              >
                <MenuItem value="">Select Week</MenuItem>
                {weeks.map((week) => (
                  <MenuItem key={week.weekNumber} value={week.weekNumber}>
                    {`Week ${week.weekNumber}: ${week.startDate} - ${week.endDate}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Create Button */}
            <Button 
              variant="contained" 
              onClick={handleCreate}
              size="small"
              sx={{ flex: 1 }}
            >
              Create
            </Button>
          </Box>
        </Box>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}
      </Paper>

      {/* Timecard Table Section */}
      {createdTimecards.length > 0 && (
        <TableContainer component={Paper} sx={{ mt: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Week</TableCell>
                {(() => {
                  if (createdTimecards.length > 0) {
                    const timecard = createdTimecards[0];
                    const timecardWeekInfo = getWeekDates(parseUTCDate(timecard.startDate));
                    return timecardWeekInfo.weekDates.map((weekDay) => (
                      <TableCell key={weekDay.full.toISOString()} align="center">
                        {weekDay.dayName}<br />
                        {weekDay.display}
                      </TableCell>
                    ));
                  }
                  return null;
                })()}
                <TableCell align="center">Total</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {createdTimecards.map((timecard) => (
                <React.Fragment key={timecard._id}>
                  <TableRow>
                    <TableCell>
                      {timecard.user ? `${timecard.user.firstName} ${timecard.user.lastName}` : 'Unknown User'}
                    </TableCell>
                    <TableCell>{timecard.project.name}</TableCell>
                    <TableCell>{timecard.dateRange}</TableCell>
                    {timecard.days.map((day, idx) => (
                      <TableCell key={idx} align="center">
                        {day.hours}
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      {timecard.days.reduce((sum, day) => sum + parseFloat(day.hours || 0), 0).toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton 
                        size="small" 
                        onClick={() => handleEdit(timecard)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small"
                        onClick={() => handleDelete(timecard._id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  {/* Comments row */}
                  {timecard.days.some(day => day.comments) && (
                    <TableRow>
                      <TableCell colSpan={3} />
                      {timecard.days.map((day, idx) => (
                        <TableCell 
                          key={idx} 
                          align="center"
                          sx={{ 
                            whiteSpace: 'pre-wrap',
                            fontSize: '0.875rem',
                            color: 'text.secondary'
                          }}
                        >
                          {day.comments || ''}
                        </TableCell>
                      ))}
                      <TableCell colSpan={2} />
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Timecard Dialog */}
      <EditTimecardAdminDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedTimecard(null);
        }}
        timecard={selectedTimecard}
        onUpdate={handleUpdate}
      />
    </Box>
  );
};

export default CreateTimecards;
